import { useEffect, useState } from 'react';

import { vnextApiClient } from '@/services/vnext-api';

const EMPTY: ReadonlySet<string> = new Set();

// Module-level once-per-session cache — the calendar changes yearly,
// not per mount.
let holidaysPromise: Promise<ReadonlySet<string>> | null = null;

/** Test hook — clears the module cache between renders. */
export function clearUkBankHolidaysCache(): void {
  holidaysPromise = null;
}

/**
 * England bank holidays as a set of "YYYY-MM-DD" strings, fetched once
 * from the backend (the single calendar authority behind the TAT ↔
 * due-date coupling). Empty until loaded — and on failure — so the
 * business-day maths degrades to weekend-only skipping rather than
 * blocking the form.
 */
export function useUkBankHolidays(): ReadonlySet<string> {
  const [holidays, setHolidays] = useState<ReadonlySet<string>>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    if (!holidaysPromise) {
      holidaysPromise = vnextApiClient
        .getUkBankHolidays()
        .then((dates) => new Set(dates) as ReadonlySet<string>)
        .catch((err) => {
          // Don't cache failures — the next mount retries.
          holidaysPromise = null;
          throw err;
        });
    }
    holidaysPromise
      .then((set) => {
        if (!cancelled) {
          setHolidays(set);
        }
      })
      .catch(() => {
        // Weekend-only fallback; nothing to surface.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return holidays;
}
